import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Title from '../components/Title';
import Copyright from "../components/CopyRight";
import * as React from "react";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useConversation } from '@elevenlabs/react';
import { Button, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useTSGUser } from "../contexts/TSGUserContext";
import { useMcpCounts } from "../hooks/useMcpCounts";
import {
    MessageList,
    Message,
    MessageProps,
    MessageSeparator,
} from "@chatscope/chat-ui-kit-react";
import { MessageDirection } from "@chatscope/chat-ui-kit-react/src/types/unions";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { CartItem, ShoppingCart } from "../models/Store";
import { generateOrderID, genUniqueId } from "../utils/GenerateUniqueID";
import { Storefront } from "../models/StorefrontResponse";
import { useProductsAll } from "../hooks/useProductsAll";
import { MCPCountsResponse, MCPListCount } from "../models/MCP";
import { useConversations } from "../hooks/useConversations";
import { APIEndpoint, EndpointType, httpPost } from "../utils/apiService";
import CloseIcon from "@mui/icons-material/Close";
import { AIConversation, ConversationMessageModel, ConversationMessageType, ConversationsResponse } from "../models/Conversation";


// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot

interface Props {
    storefront: Storefront
}

const Conversation = (props: Props) => {
    const [activeConversation, setActiveConversation] = useState(false)
    const { tsgUser } = useTSGUser();
    const [messageList, setMessageList] = useState<MessageProps[]>([]);
    const [zip, setZip] = useState("");
    const [previousConversationID, setPreviousConversationID] = useState<string>(""); // Replace 'any' with your conversation type if available
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [snackbarMsg, setSnackbarMsg] = useState("")
    const [conversationID, setConversationID] = useState<string>("");
    const [conversationsRefreshKey, setConversationsRefreshKey] = useState(1);
    const [doSaveConversation, setDoSaveConversation] = useState(false);

    const { listCounts } = useMcpCounts(zip, "", "", "", "", false, 1, tsgUser.id || "");
    const { productsAll } = useProductsAll(props.storefront.storeName, 1)
    const { conversations } = useConversations(tsgUser.id || "", conversationsRefreshKey);

    const [shoppingCart, setShoppingCart] = useState<ShoppingCart>({
        cartToken: genUniqueId(),
        createdAt: "",
        modifiedAt: "",
        hubspotDealID: "",
        subtotal: 0,
        total: 0,
        discount: 0,
        couponCode: "",
        items: [],
        status: "shopping",
        userID: tsgUser.id || "",
        storefrontID: props.storefront.storeName,
        discountDescription: "",
        orderID: generateOrderID(),
        applyWallet: false,
        walletCreditsApplied: 0
    })

    const conversation = useConversation({
        onConnect: () => {
            console.log(`Conversation started`);
            setActiveConversation(true);
            setMessageList([]); // Clear previous messages when starting a new conversation
            addSystemMessage(`Conversation started`);
        },
        onEnd: () => {
            console.log("Conversation ended");
        },
        onMessage: (message) => {
            console.log("New message:", message);
            if (message.source === 'ai') {
                addTextMessageBox("Ellie", "incoming", message.message);

                // If the text contains "Have a wonderful day", pause 3 seconds then end the conversation
                if (message.message.toLowerCase().includes("have a wonderful day")) {
                    setTimeout(() => {
                        handleEndConversation();
                    }, 3000);
                }
            } else {
                addTextMessageBox(tsgUser.firstName || "You", "outgoing", message.message);
                detectZipCode(message.message); // Detect zip code from user message
            }
        },
        onError: (error) => {
            console.error("Error in conversation:", error);
        },
        onDisconnect: () => {
            console.log(`Conversation ended`);
            addSystemMessage(`Conversation ended`);
            setActiveConversation(false);
            setDoSaveConversation(true); // Set flag to save conversation
        }
    });

    useEffect(() => {
        // Log Pageview with Hubspot
        const _hsq = window._hsq = window._hsq || [];
        _hsq.push(['setPath', '/conversation']);
        _hsq.push(['trackPageView']);
    }, []);

    useEffect(() => {
        if (shoppingCart.items.length > 0) {
            console.log("Shopping Cart Updated:", shoppingCart);
        }
    }, [shoppingCart]);

    useEffect(() => {
        if (!doSaveConversation) return;
        console.log("Saving conversation...");
        saveConversation();
    }, [doSaveConversation]);

    useEffect(() => {
        if (listCounts && listCounts.counts && listCounts.counts.length > 0) {
            console.log("List Counts:", listCounts);
            const newMessage: MessageProps = {
                model: {
                    sentTime: new Date().toISOString(),
                    sender: "Ellie's Brain Data",
                    direction: "incoming",
                    position: "normal",
                    type: "custom",
                    payload: countDisplayReact(listCounts), // Use the React version of the count display
                }
            };

            setMessageList(prevMessages => [...prevMessages, newMessage]);
        }
    }, [listCounts]);

    function addItemToCart(item: MCPListCount, zipCode: string = "") {
        // iterate through productsAll to find the product that matches the listType
        const prod = productsAll.find(p => p.audienceID === item.audienceID);

        if (!prod) {
            console.error("Product not found for audienceID:", item.audienceID);
            return;
        }

        const cartItem: CartItem = {
            id: genUniqueId(),
            product: prod,
            quantity: item.count,
            postalCodes: [zipCode]
        }

        // Add the item to the shopping cart
        setShoppingCart(prevCart => {
            const updatedCart = { ...prevCart };
            updatedCart.items.push(cartItem);
            updatedCart.subtotal += prod.price * item.count; // Update subtotal
            updatedCart.total = updatedCart.subtotal; // Update total (no discounts applied yet)
            updatedCart.modifiedAt = new Date().toISOString(); // Update modifiedAt timestamp
            updatedCart.userID = tsgUser.id || ""; // Set user ID from TSG
            localStorage.setItem("cart", JSON.stringify(updatedCart)); // Save to local storage
            console.log("Product added to cart:", cartItem);
            return updatedCart;
        });
    }

    // const getDataTableBackground = () => {
    //     if (props.storefront.activeColors === props.storefront.darkMode) {
    //         return "#c7e4fa"
    //     }
    //     return "#c7e4fa";
    // }

    const countDisplayReact = (lcResponse: MCPCountsResponse) => {
        if (!lcResponse || !lcResponse.counts || lcResponse.counts.length === 0) {
            return <Typography variant="body2">No available leads found.</Typography>;
        }
        const lc = Array.isArray(lcResponse.counts) ? lcResponse.counts : [];
        // sort counts by type is ascending order
        lc.sort((a, b) => a.listType.localeCompare(b.listType));
        return (
            <div>
                <b>Available Leads in {lcResponse.city}, {lcResponse.state} {lcResponse.zip}</b>
                <TableContainer component={Paper} sx={{ background: "#c7e4fa" }}>
                    <Table sx={{ borderCollapse: "collapse" }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }}>List Type</TableCell>
                                <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }}>Available</TableCell>
                                <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                lc.map(count => (
                                    <TableRow key={count.listType} >
                                        <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }}>{count.listType}</TableCell>
                                        <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }}>{count.count.toLocaleString()}</TableCell>
                                        <TableCell sx={{ color: props.storefront.darkMode.mainPage.headerBackground }} align="right">
                                            <IconButton sx={{ height: 28, color: props.storefront.darkMode.mainPage.headerBackground }} onClick={() => addItemToCart(count, lcResponse.zip)}>
                                                <AddShoppingCartIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <div id="listCounts" style={{ display: "none" }}>{JSON.stringify(listCounts)}</div>
            </div>
        )
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function extractListCounts(json: any): MCPCountsResponse {
        if (!json || !json.props || !Array.isArray(json.props.children)) return {} as MCPCountsResponse;

        for (const child of json.props.children) {
            if (
                child &&
                child.props &&
                child.props.id === "listCounts" &&
                typeof child.props.children === "string"
            ) {
                try {
                    const lcResponse = JSON.parse(child.props.children) as MCPCountsResponse;
                    return lcResponse;
                } catch (e) {
                    console.error("Failed to parse listCounts JSON:", e);
                    return {} as MCPCountsResponse;
                }
            }
        }
        return {} as MCPCountsResponse;
    }

    const handleStartConversation = async () => {
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // For public agents, pass in the agent ID and the connection type
        const conversationId = await conversation.startSession({
            agentId: 'agent_01k058yq13eqfsywmxh4be8px2',
            connectionType: 'webrtc', // either "webrtc" or "websocket"
            dynamicVariables: {
                first_name: tsgUser.firstName || "There",
            }
        });

        console.log("Conversation started with ID:", conversationId);
        setConversationID(conversationId);
    }

    const handleEndConversation = async () => {
        await conversation.endSession();
        console.log("Conversation ended");
    };

    // detectZipCode detects a zip code from the text and sets it in the state so we can use it to fetch counts
    const detectZipCode = (text: string) => {
        const zipCodePattern = /\b\d{5}(?:-\d{4})?\b/; // Matches 5-digit or 9-digit zip codes
        const match = text.match(zipCodePattern);
        if (match) {
            setZip(match[0]);
            console.log("Detected Zip Code:", match[0]);
        }
    }

    const addTextMessageBox = (sender: string, direction: MessageDirection, message: string) => {

        const newMessage: MessageProps = {
            model: {
                message: message,
                sentTime: new Date().toISOString(),
                sender: sender,
                direction: direction,
                position: "last"
            }
        };

        setMessageList(prevMessages => [...prevMessages, newMessage]);
    }

    const addSystemMessage = (text: string) => {
        const newMessage: MessageProps = {
            model: {
                message: text,
                sentTime: new Date().toISOString(),
                sender: "System",
                direction: "incoming",
                position: "normal"
            }
        };

        setMessageList(prevMessages => [...prevMessages, newMessage]);
    }

    const handlePreviousConversationChange = (event: SelectChangeEvent) => {
        const selectedConversationID = event.target.value as string;
        setPreviousConversationID(selectedConversationID);
        console.log("Selected previous conversation ID:", selectedConversationID);

        // Load the selected conversation messages
        const selectedConversation = conversations.find(c => c.id === selectedConversationID);
        if (selectedConversation) {
            console.log("Loading messages for conversation:", selectedConversation);
            const messageProps: MessageProps[] = selectedConversation.messages.map(msg => ({
                model: createModelFromMessage(msg) as ConversationMessageModel
            }));
            setMessageList(messageProps);
        } else {
            console.warn("Selected conversation not found:", selectedConversationID);
            setMessageList([]);
        }
    };

    const createModelFromMessage = (model: ConversationMessageModel): ConversationMessageModel => {
        const newModel: ConversationMessageModel = {
            sentTime: model.sentTime || new Date().toISOString(),
            sender: model.sender,
            direction: model.direction || "incoming",
            position: model.position || "normal",
            type: model.type as ConversationMessageType, // Ensure type is set correctly
        };

        if (model.type === "custom" && model.payload) {
            //newModel.payload = jsonToReact(model.payload);
            //console.log("Custom payload detected:", model.payload);
            const lcResponse = JSON.parse(model.payload as string) as MCPCountsResponse;

            //console.log("Parsed List Counts:", lcResponse);
            newModel.payload = countDisplayReact(lcResponse); // Use the React version of the count display
        } else {
            newModel.message = model.message || "";
        }
        return newModel;
    }


    const saveConversation = () => {
        setDoSaveConversation(false); // Reset the save flag
        if (conversationID === "") {
            setSnackbarMsg("No conversation to save");
            setOpenSnackbar(true);
            return;
        }

        const newConversation: AIConversation = {
            id: "",
            customerID: tsgUser.id || "",
            createdAt: "",
            modifiedAt: "",
            conversationID: conversationID,
            messages: []
        }

        // Iterate through messageList and convert to ConversationMessage
        messageList.forEach(msg => {
            const model = msg.model;
            if (model) {
                const conversationMessage: ConversationMessageModel = {
                    message: model.message,
                    sentTime: model.sentTime,
                    sender: model.sender,
                    direction: model.direction,
                    position: model.position,
                    type: model.type as ConversationMessageType, // Ensure type is set correctly
                    payload: model.payload ? JSON.stringify(extractListCounts(model.payload)) : undefined // Handle payload if it exists
                };
                newConversation.messages.push(conversationMessage);
                console.log("Added message to conversation:", conversationMessage);
            } else {
                console.warn("Message model is undefined, skipping message:", msg);
            }
        });

        const apiURL = APIEndpoint(EndpointType.Conversations);
        httpPost(apiURL, JSON.stringify(newConversation))
            .then((data) => {
                const response = data as ConversationsResponse;

                if (response.status == 'error') {
                    setSnackbarMsg("Error saving conversation: " + response.errorMessage)
                    setOpenSnackbar(true)
                } else {
                    setSnackbarMsg("Conversation saved successfully")
                    setOpenSnackbar(true)
                    setConversationsRefreshKey(prevKey => prevKey + 1); // Trigger a refresh of conversations
                }
            })
            .catch((error) => {
                setSnackbarMsg(`List was not saved - error: ${error as string}`)
                setOpenSnackbar(true)
            });
    }

    const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSnackbar(false);
    };

    // formatLocalDateTime formats the date and time in a human-readable format
    const formatLocalDateTime = (dateTime: string) => {
        const date = new Date(dateTime);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };


    const snackbarAction = (
        <React.Fragment>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleSnackbarClose}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                        <Title>Sales Agent - Ellie</Title>
                        <Grid item xs={12}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Ellie is The Share Groups AI Agent. Her name is derived from “L.E.” (Lead Expert) and you can ask her questions about our products, available lead counts, pricing, or any other inquiries you might have.
                            </Typography>

                            {!activeConversation ?
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }} justifyContent="space-between" alignItems={"flex-end"}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleStartConversation}
                                        sx={{ mt: 2 }}
                                    >
                                        Start Conversation
                                    </Button>
                                    {conversations.length > 0 &&
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <InputLabel id="previous-conversations-label">Previous Conversations</InputLabel>
                                            <Select
                                                labelId="previous-conversations-label"
                                                id="previous-conversations"
                                                value={previousConversationID}
                                                label="Previous Conversations"
                                                onChange={handlePreviousConversationChange}
                                            >
                                                {conversations.map((conversation) => (
                                                    <MenuItem key={conversation.id} value={conversation.id}>
                                                        {formatLocalDateTime(conversation.createdAt)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    }
                                </Stack>
                                : <Button variant="outlined" color="secondary" onClick={handleEndConversation} sx={{ mt: 2 }}>
                                    End Conversation
                                </Button>
                            }
                            <Stack sx={{ mt: 2 }} direction="row" spacing={2} >
                                <MessageList style={{ paddingTop:5, width: "100%", height: "100%", background: props.storefront.activeColors.mainPage.headerBackground }}>
                                    {messageList.length > 0 && 
                                        <MessageSeparator>{messageList[0].model?.sentTime ? formatLocalDateTime(messageList[0].model.sentTime) : new Date().toISOString()   }</MessageSeparator>
                                    }
                                    {messageList.map((m, i) =>
                                        <Message key={i} {...m}/>
                                    )}
                                </MessageList>
                            </Stack>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            <Copyright sx={{ pt: 4 }} />
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={snackbarMsg}
                action={snackbarAction}
            />
        </Container>
    );
};

export default Conversation;
